// Admin pre-bid delta payload + mapper for the obscured admin channel
// `admin:auction:{id}:maxbids:{token}` (BL-1325).
//
// WHY THIS EXISTS. The clerk console's bid record shows a lot's pre-bids — the
// stored maximums placed before the auction opened — and a clerk correcting one
// from another session must see the change land without a refresh (AC 5). The
// three admin channels PRJ-48 shipped don't carry it: the de-anon bid ledger
// reads `bids`, and a pre-bid creates no `bids` row at all (it is a `max_bids`
// row with `placed_during_pre_bid = true`, written by `PlacePreBidMaxService`).
//
// FACTS ON THE WIRE, IDENTITY OFF IT. This payload carries the max-bid facts —
// amount, lifecycle status, which lot, which customer — and NO bidder identity:
// no name, no paddle number. The console resolves those from `useAuctionBidders`
// (`GET /api/auctions/[id]/bidders`), the same authenticated read the bidder rail
// and the Bidders tab already use, joined client-side on `customerId`.
//
// That split is a deliberate design call (Michael Lawrie, 2026-08-23): *"realtime
// only for max bid, fetch the rest once on first need."* A bidder's name and
// paddle don't change mid-sale, so paying to keep them live buys nothing, while
// putting them on a channel costs real confidentiality surface. It also means
// this publisher needs no `customers` join at all. Same posture, and the same
// reasoning, as the registrations channel's identifiers-only payload (BL-1262).
//
// STILL CONFIDENTIAL, STILL OBSCURED. Dropping identity narrows the blast radius
// but does not make this topic public — a bidder's stored maximum is the single
// most sensitive number on the clerk's screen (it is the ceiling their proxy will
// bid to), and this payload carries it alongside the customer holding it. It rides
// ONLY the HMAC-obscured admin channel and must never reach a bidder surface.
//
// SHAPE. Current-state LWW, like `admin-lot-state.ts` and
// `admin-auction-registrations.ts` — NOT the append-only `(createdAt, id)` ledger
// model the bid ledger uses. That choice follows the storage: `bids` rows are
// effectively immutable once written, but `max_bids` rows are UPDATEd in place by
// `AdminPreBidService` (amount on an edit, `status = 'cancelled'` on a delete), so
// "latest version of row X wins" is what actually happens to the data. A
// correction re-broadcasts the same `maxBidId` with a higher `sequence` and the
// client replaces its entry. NOT event sourcing — `max_bids` stays the source of
// truth and the catch-up route re-reads it.
//
// KEEP IT LWW (BL-1629 RAC 2). The sequence added below is an ORDERING key, not a
// log: it re-numbers the same row in place rather than appending. Folding this
// stream as an append-only feed would render a ceiling that has since been
// cancelled or outbid, because the superseding row is a mutation of the same
// `maxBidId`, not a new entry after it.

import type Decimal from 'decimal.js';

import type { MoneyWire } from '../../isomorphic/money/codec';
import type { Money } from '../../isomorphic/money/money';
import { decodeWireAmount, encodeRowMoney } from './money-wire-amount';

/** The `max_bid_status` lifecycle values this payload knows how to carry. */
export type AdminMaxBidStatus =
    | 'active'
    | 'outbid'
    | 'won'
    | 'cancelled'
    | 'superseded';

// BL-1397: this set MUST track `max_bid_status` exactly. It is a runtime
// string set, so tsc cannot see through it to the DB enum — a value left stale
// here makes `mapAdminMaxBid` return null for every row carrying it, which
// drops the delta from the admin stream and silently freezes the clerk
// console's pre-bid row instead of failing loudly.
const VALID_STATUSES: ReadonlySet<string> = new Set<AdminMaxBidStatus>([
    'active',
    'outbid',
    'won',
    'cancelled',
    'superseded',
]);

/**
 * One changed pre-bid's current state on the admin pre-bids stream.
 *
 * `sequence` is the ceiling's position in its LOT's monotonic series, claimed from
 * `ledger_topic_seq` and stamped on `admin_max_bid_seq` by the batch finalizer at the
 * END of the mutating transaction — so the series is in COMMIT order, dense per lot,
 * and strictly increasing. It is both the last-writer-wins ordering key the client
 * folds on (highest sequence per `maxBidId` wins) and the topic-level gap detector.
 *
 * **This replaced an `updated_at` cursor (BL-1629), and the reason is worth keeping.**
 * `set_updated_at()` writes `now()` — transaction-START time — so a ceiling written by
 * a slow transaction carried a cursor BELOW a client's, and catch-up's `>= cursor`
 * filter could never return it. Not late: never. Absolute-LWW makes a re-delivered
 * message idempotent; it does nothing for a skipped one, and nothing re-sent it. A
 * commit-ordered sequence closes both that skew and the undetectability of a dropped
 * delta, which is why one field does both jobs here.
 */
export interface AdminMaxBidDelta {
    /** `max_bids.id`. The fold key. */
    maxBidId: string;
    /**
     * `max_bids.lot_id` — the lot this ceiling is on, and the topic's scope.
     *
     * NOTE there is deliberately no `auctionId` (BL-1552). The feed is per-lot,
     * so an auction id on the payload is both unreadable-by-anything and an
     * invitation to reason auction-wide on a lot-scoped stream — which is the
     * exact model this stream was rebuilt to remove. A consumer that genuinely
     * needs the auction resolves it from the lot.
     */
    lotId: string;
    /**
     * CONFIDENTIAL — `max_bids.customer_id`. The join key the console uses to
     * resolve a name and paddle from `useAuctionBidders`. Admin channel only.
     */
    customerId: string;
    /**
     * CONFIDENTIAL — the bidder's stored ceiling in its own currency
     * (`max_bids.max_amount` + `max_bids.currency`), e.g.
     * `{ amount: "500.0000", currency: "USD" }`. Encoded once in
     * {@link mapAdminMaxBid} by the shared `encodeRowMoney`, so the live delta
     * and the catch-up carry identical bytes (BL-2010, ADR 060 — Money Type
     * § 2.3). The client decodes it once with {@link decodeAdminMaxBidDelta}.
     */
    maxAmount: MoneyWire;
    /** The row's CURRENT lifecycle state — drives the superseded row treatment. */
    status: AdminMaxBidStatus;
    /**
     * `max_bids.placed_during_pre_bid` — TRUE iff this maximum was placed before
     * the auction opened. This flag, not a timestamp comparison against the
     * earliest bid, is what puts a row below the divider (AC 1).
     */
    placedDuringPreBid: boolean;
    /** `max_bids.placed_by_clerk` — marks a max a clerk entered on the bidder's behalf. */
    placedByClerk: boolean;
    /** `max_bids.created_at` — when the maximum was placed; shown on the row. */
    createdAt: string;
    /**
     * The ceiling's position in its LOT's monotonic sequence, from
     * `admin_max_bid_seq.sequence` (BL-1629).
     *
     * Does double duty, which is the point: it is the last-writer-wins ordering key
     * the client folds on (highest sequence per `maxBidId` wins) AND the topic-level
     * gap detector (a client holding N applies N+1 directly and catches up on N+2).
     */
    sequence: number;
}

/**
 * Result of the admin max-bid catch-up route
 * (`createAdminLotMaxBidsCatchupHandler`): the minted obscured `topic`, the
 * current state of every changed `max_bids` row on the lot, and the max `sequence`
 * in that set (the client's next `since_sequence`).
 *
 * It reports its position under `sequence` rather than `cursor` because this stream's
 * ordering key is a dense per-LOT counter claimed in commit order — which is exactly
 * the signal `EntityStateStore` reads to turn gap detection on (BL-1628). Declared
 * here, beside the delta, so the route registry types the response (ADR 027 —
 * Response Contract) without importing the client store.
 */
export interface MaxBidStateCatchupResult {
    topic: string;
    maxBids: AdminMaxBidDelta[];
    sequence: number;
}

/**
 * Input row shape — duck-typed to the Drizzle SELECT the publisher and the
 * catch-up factory both run against `max_bids`. Listing only the fields the
 * mapper reads keeps callers free to project wider rows without dragging
 * anything extra through the mapper. `sequence` is read in SQL from the joined
 * `admin_max_bid_seq` row, so the mapper stays a pure reshape with no ordering
 * logic of its own.
 */
export interface AdminMaxBidRow {
    maxBidId: string;
    lotId: string;
    customerId: string;
    /** `max_bids.max_amount` — numeric(18,4) decoded to an exact Decimal by the
     *  moneyNumeric custom type (ADR 060 — Money Type, BL-1937). */
    maxAmount: Decimal;
    /** `max_bids.currency` — the row's own ISO 4217 code (BL-1937). Rule #40:
     *  a column of the same `max_bids` row, so it adds no fan-in source. */
    currency: string;
    status: string;
    placedDuringPreBid: boolean;
    placedByClerk: boolean;
    createdAt: string;
    sequence: number;
}

/**
 * Map a `max_bids` source row to the admin pre-bid delta shape. Pure reshape —
 * no I/O, no ordering logic (the caller reads `sequence` in SQL).
 *
 * DELIBERATELY an explicit field list, not a spread. `max_bids` gained
 * `correction_reason` in BL-1325 — an operator's free-text explanation for a
 * correction, which has no business on a broadcast channel — and a spread would
 * have shipped it the moment the column landed. The exact-key-set case in this
 * module's test is what holds that line.
 *
 * @param row - the projected `max_bids` row.
 * @returns the delta, or `null` when the row's status isn't one this payload
 *   knows. Throws on an unsupported currency (BL-2010): a ceiling silently
 *   missing from the clerk's record is worse than a logged publish failure. A dropped entry is recoverable (the catch-up re-reads current state);
 *   an entry whose status the client folds into a branch it doesn't have is not.
 * @sideEffect none — pure computation.
 */
export function mapAdminMaxBid(row: AdminMaxBidRow): AdminMaxBidDelta | null {
    if (!VALID_STATUSES.has(row.status)) return null;
    return {
        maxBidId: row.maxBidId,
        lotId: row.lotId,
        customerId: row.customerId,
        maxAmount: encodeRowMoney({ amount: row.maxAmount, currency: row.currency }),
        status: row.status as AdminMaxBidStatus,
        placedDuringPreBid: row.placedDuringPreBid,
        placedByClerk: row.placedByClerk,
        createdAt: row.createdAt,
        sequence: row.sequence,
    };
}

/**
 * A max-bid delta as the clerk console holds it: the wire ceiling decoded into a
 * `Money` once, at the hook boundary, so no component or picker re-parses it or
 * pairs it with a currency from anywhere else (BL-2010, ADR 060 — Money Type).
 */
export type AdminMaxBidDeltaDecoded = Omit<AdminMaxBidDelta, 'maxAmount'> & {
    maxAmount: Money;
};

/** How one max-bid delta decoded. */
export type AdminMaxBidDeltaDecodeResult =
    | { kind: 'ok'; delta: AdminMaxBidDeltaDecoded }
    | { kind: 'undecodable' };

/**
 * Decode one max-bid delta's wire ceiling (BL-2010).
 *
 * @param delta - The delta as it arrived (broadcast or catch-up).
 * @returns The decoded delta, or `undecodable` when the ceiling is not a
 *   well-formed MoneyWire. Never throws: the caller logs and skips the row.
 */
export function decodeAdminMaxBidDelta(delta: AdminMaxBidDelta): AdminMaxBidDeltaDecodeResult {
    const decoded = decodeWireAmount(delta.maxAmount);
    if (decoded.kind !== 'money') return decoded;
    return { kind: 'ok', delta: { ...delta, maxAmount: decoded.money } };
}
